import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setRadioValue } from "../redux/slices/radioSlice"; // Using radioSlice for dropdown values
import InfoTooltip from "./Tooltip";

const DropdownSelect = ({ label, description, options, stateName, tooltip, defaultValue = null, searchable = false, error }) => {
  const dispatch = useDispatch();
  const selectedValue = useSelector((state) => state.radio.selectedValues[stateName] || "");

  const [searchTerm, setSearchTerm] = useState(""); // Search input state
  const [filteredOptions, setFilteredOptions] = useState(options); // Filtered options
  const [customOption, setCustomOption] = useState(null); // Store **only one** user-inputted option
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // Dropdown visibility
  const [hoveredIndex, setHoveredIndex] = useState(null); // Track the hovered option

  const dropdownRef = useRef(null);

  const handleMouseEnter = (index) => setHoveredIndex(index);
  const handleMouseLeave = () => setHoveredIndex(null);

  const highlightMatch = (text) => {
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm})`, "gi");
    return text.replace(regex, "<span style='color: red; font-weight: bold;'>$1</span>");
  };

  // Set default value when provided
  useEffect(() => {
    if (defaultValue !== null && String(selectedValue) !== String(defaultValue)) {
      dispatch(setRadioValue({ stateName, value: Number(defaultValue) }));
    }
  }, [defaultValue, selectedValue, dispatch, stateName]);  

  // Get the label of the selected value
  const allOptions = useMemo(() => [...options, ...(customOption ? [customOption] : [])], [options, customOption]);
  const selectedLabel = allOptions.find(opt => String(opt.value) === String(selectedValue))?.label || "Select an option";

  // Update filtered options based on search term
  useEffect(() => {
    if (!searchable || searchTerm.trim() === "") {
      setFilteredOptions(options);
    } else {
      const lowerSearch = searchTerm.toLowerCase();
      setFilteredOptions(
        allOptions.filter(option => option.label.toLowerCase().includes(lowerSearch))
      );
    }
  }, [searchTerm, searchable, options, customOption, allOptions]);

  // Handle selection change
  const handleSelect = (value) => {
    dispatch(setRadioValue({ stateName, value: Number(value) }));
    setIsDropdownOpen(false); // Close dropdown after selection
    setHoveredIndex(null); // Reset hover state
  };

  // Handle custom input submission (overwrite existing custom input)
  const handleCustomInput = () => {
    if (searchTerm.trim() !== "") {
      const newValue = Math.max(...options.map(opt => Number(opt.value)), 0) + 1; // Increment last value
      const newOption = { label: searchTerm, value: String(newValue) };
      
      setCustomOption(newOption); // Overwrite existing custom input
      dispatch(setRadioValue({ stateName, value: newValue })); // Set new value
      setSearchTerm(""); // Reset search
      setIsDropdownOpen(false); // Close dropdown
    }
  };

  // Toggle dropdown visibility
  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div data-field={stateName} className={`mt-3 ${error ? "thick-error-border" : "radio-group-container"}`} ref={dropdownRef}>
      <fieldset className="form-section">
        <legend className="mb-3" style={{ color: 'black' }}>
          <span
            dangerouslySetInnerHTML={{ __html: label }}
          />
          {tooltip && <InfoTooltip text={tooltip} />}
        </legend>
        
        {description && (
          <div
            className="text-muted mb-4 ps-2 border-start border-3 border-secondary"
            style={{ fontSize: "0.95rem", lineHeight: "1.6" }}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}

        {/* Custom Styled Dropdown */}
        <div className="dropdown drop-up">
          <button 
            className="btn btn-light dropdown-toggle w-100 text-start" 
            type="button" 
            onClick={toggleDropdown}
          >
            {selectedLabel}
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="dropdown-menu show w-100 p-2 shadow" style={{ bottom: "100%", top: "auto" }}>
              
              {/* Search Box Inside Dropdown */}
              {searchable && (
                <div className="d-flex align-items-center mb-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {/* Clear (X) Button */}
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary ms-2"
                      onClick={() => setSearchTerm("")}
                    >
                      ❌
                    </button>
                  )}
                </div>
              )}

              {/* Options List */}
              <div className="options-container" style={{ maxHeight: "200px", overflowY: "auto" }}>
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option, index) => (
                    <div 
                      key={index} 
                      className={`dropdown-item ${hoveredIndex === index ? "highlighted-option" : ""}`}
                      onMouseEnter={() => handleMouseEnter(index)}
                      onMouseLeave={handleMouseLeave}
                      onClick={() => handleSelect(option.value)}
                      style={{ cursor: "pointer" }}
                    >
                      <span dangerouslySetInnerHTML={{ __html: highlightMatch(option.label) }} />
                    </div>
                  ))
                ) : (
                  <div className="dropdown-item text-muted">No results found</div>
                )}
              </div>

              {/* Custom Input Option (Overwrite Existing Custom Input) */}
              {searchable && searchTerm && !filteredOptions.length && (
                <div className="dropdown-item">
                  <button className="btn btn-outline-primary w-100" onClick={handleCustomInput}>
                    Add "{searchTerm}"
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </fieldset>
    </div>
  );
};

export default DropdownSelect;
